// AdminPasskeyGate — full-screen blocker shown to eleplan-admin users when
// they have no passkey registered for the current origin. Privileged admin
// actions (SSM reveal/edit/delete, etc.) require a fresh WebAuthn assertion;
// without an origin-scoped passkey the action silently fails with
// "Biometric prompt cancelled" (see SsmParamsScreen.requireAssertion).
//
// WebAuthn passkeys are origin-bound, so a key registered on app.eleplan.com
// does not unlock anything on development.eleplan.org. The gate forces admins
// to register a key for THIS origin before doing anything else.
//
// Lifts itself off the screen automatically when the user lands on the
// Settings screen (so the underlying passkey UI is interactive) and when
// the passkey list gains an origin-matching credential.

import React, { useEffect, useMemo, useState } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { navigationRef } from '../navigation/navigationRef';
import { useTheme } from '../hooks/useTheme';
import Icon from './Icon';
import { isPasskeySupported } from '../utilities/passkeys';
import { signOut } from '../store/actions/auth';
import type { RootState } from '../store';

function getCurrentOrigin(): string | null {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return null;
}

function getCurrentRouteName(): string | undefined {
  try {
    return navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : undefined;
  } catch {
    return undefined;
  }
}

export default function AdminPasskeyGate() {
  const theme = useTheme();
  const dispatch = useDispatch();

  const user = useSelector((s: RootState) => (s as any).authentication?.user);
  const account = useSelector((s: RootState) => (s as any).session?.account);
  const locked = useSelector((s: RootState) => (s as any).session?.locked);

  const authorized = !!user?.cognito_id && !!account?.account_id;
  const isEleplanAdmin =
    Array.isArray(account?.permissions) && account.permissions.includes('eleplan-admin');

  // Backend `/users/get-user` returns each passkey flat: `{ id, origin, name,
  // created_at, last_used, status }`. There is no nested `credential.id` — the
  // credential id is the top-level `id`. Reading `pk.credential?.id` here
  // (matching an older nested shape) silently drops every passkey from the
  // filter, which is why the gate kept showing "Passkey Required" right after
  // the user successfully added a key.
  const passkeys: Array<{ id?: string; origin?: string }> = Array.isArray(user?.passkeys)
    ? user.passkeys
    : [];

  const currentOrigin = getCurrentOrigin();
  const isWeb = currentOrigin !== null;
  const hasOriginPasskey = useMemo(() => {
    const withCredential = passkeys.filter((pk) => !!pk?.id);
    if (!isWeb) return withCredential.length > 0;
    return withCredential.some((pk) => !pk?.origin || pk.origin === currentOrigin);
  }, [passkeys, isWeb, currentOrigin]);

  // Re-render on navigation state changes so the gate hides while the user is
  // on the Settings screen actively adding a passkey.
  const [, bumpNavTick] = useState(0);
  useEffect(() => {
    if (!navigationRef.isReady()) {
      // Try again on next tick — navigation may not be ready on first paint.
      const t = setTimeout(() => bumpNavTick((n) => n + 1), 100);
      return () => clearTimeout(t);
    }
    const unsubscribe = navigationRef.addListener('state', () => bumpNavTick((n) => n + 1));
    return unsubscribe;
  }, []);

  const routeName = getCurrentRouteName();
  const onSettings = routeName === 'SettingsHome' || routeName === 'Settings';

  const shouldShow =
    authorized &&
    isEleplanAdmin &&
    isPasskeySupported() &&
    !hasOriginPasskey &&
    !locked?.show &&
    !onSettings;

  if (!shouldShow) return null;

  const handleAddPasskey = () => {
    (navigationRef as any).navigate('Main', {
      screen: 'Settings',
      params: { screen: 'SettingsHome', params: { initialTab: 'passkeys' } },
    });
  };

  const handleSignOut = () => {
    dispatch(signOut() as any);
  };

  const platformLabel =
    Platform.OS === 'ios'
      ? 'Face ID / Touch ID'
      : Platform.OS === 'android'
        ? 'fingerprint or face unlock'
        : 'Touch ID, Windows Hello, or your device passkey';

  return (
    <View
      style={[
        StyleSheet.absoluteFill,
        { backgroundColor: theme.colors.background, zIndex: 9998 },
      ]}
    >
      <SafeAreaView
        style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 }}
      >
        <Icon icon={['fad', 'fingerprint']} size={52} color={theme.colors.primary} />
        <Text
          style={{
            fontSize: 22,
            fontWeight: '700',
            color: theme.colors.text,
            marginTop: 20,
            textAlign: 'center',
          }}
        >
          Passkey Required
        </Text>
        <Text
          style={{
            fontSize: 15,
            color: theme.colors.muted,
            marginTop: 10,
            textAlign: 'center',
            lineHeight: 22,
            maxWidth: 380,
          }}
        >
          Eleplan admin actions on this device need a passkey. Register one with{' '}
          {platformLabel} to continue.
          {currentOrigin
            ? `\n\nPasskeys are bound to ${currentOrigin.replace(/^https?:\/\//, '')}, so a key from another environment won't work here.`
            : ''}
        </Text>

        <TouchableOpacity
          onPress={handleAddPasskey}
          style={[gateStyles.button, { backgroundColor: theme.colors.primary, marginTop: 32 }]}
        >
          <Text style={gateStyles.buttonText}>Add Passkey</Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={handleSignOut}
          style={[
            gateStyles.button,
            gateStyles.buttonOutline,
            { borderColor: theme.colors.border, marginTop: 12 },
          ]}
        >
          <Text style={[gateStyles.buttonText, { color: theme.colors.muted }]}>Sign Out</Text>
        </TouchableOpacity>
      </SafeAreaView>
    </View>
  );
}

const gateStyles = StyleSheet.create({
  button: {
    width: 220,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
  },
});
